import { useState, useEffect } from 'react';
import { Briefcase, Clock, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { ConsultantSpaceReview } from './ConsultantSpaceReview';

interface SpacePlanningReviewsListProps {
  accessToken: string;
  consultantId: string;
}

interface PendingReview {
  companyId: string;
  companyName: string;
  submittedAt: string;
  submittedBy: string;
  suggestedSpacesCount: number;
}

export function SpacePlanningReviewsList({ accessToken, consultantId }: SpacePlanningReviewsListProps) {
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  useEffect(() => {
    loadPendingReviews();
  }, []);

  const loadPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-planning/pending-reviews`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingReviews(data.reviews || []);
      } else {
        console.error('Failed to load pending reviews');
      }
    } catch (error) {
      console.error('Error loading pending reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewComplete = () => {
    setReviewDialogOpen(false);
    setSelectedCompanyId(null);
    loadPendingReviews();
    toast.success('Space plan review completed');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="size-5 text-blue-600" />
            Space Planning Reviews
          </CardTitle>
          <CardDescription>Review and approve client space architecture plans</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-8 animate-spin text-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="size-5 text-blue-600" />
                Client Submissions Awaiting Review
                {pendingReviews.length > 0 && (
                  <Badge className="ml-2 bg-orange-100 text-orange-700 border-orange-200">
                    {pendingReviews.length} pending
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>Items submitted by clients that require approval before configuration can proceed.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadPendingReviews}>
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {pendingReviews.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-4 text-green-700 bg-green-50 rounded-lg border border-green-100">
              <CheckCircle2 className="size-5" />
              <p className="font-medium text-sm">Nothing waiting for client submission review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingReviews.map((review) => (
                <Card key={review.companyId} className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900">{review.companyName}</h4>
                          <Badge variant="outline" className="text-xs">
                            {review.suggestedSpacesCount} space{review.suggestedSpacesCount > 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="size-4" />
                            <span>Submitted {formatDate(review.submittedAt)}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedCompanyId(review.companyId);
                          setReviewDialogOpen(true);
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedCompanyId && (
            <ConsultantSpaceReview
              companyId={selectedCompanyId}
              accessToken={accessToken}
              consultantId={consultantId}
              onComplete={handleReviewComplete}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
